import { useTranslation } from "react-i18next";

import {
  CC_PHASES,
  type CcObjectDeployStatus,
  type CcObjectManifestHistory,
  type CcParityStatus,
  type CcPhase,
  type CcRequiredPhase,
} from "@/api/code-companion";
import {
  useCcCorrections,
  useCcDeployStatus,
  useCcGraph,
  useCcManifests,
  useCcParity,
} from "@/hooks/useCodeCompanion";
import {
  CC_COL_DIVIDER,
  CC_HAIRLINE,
  CC_ROW_DIVIDER,
  CC_TABLE_HEAD_BG,
} from "@/lib/cc-trace-tokens";
import { formatDuration } from "@/lib/code-companion-session";
import { tp } from "@/lib/plural";
import { type CcObjectRef, refOf, sameObject } from "@/lib/cc-object-ref";

/**
 * Matriz de frota: uma linha por objeto, uma coluna por fase.
 *
 * Do design "Code Companion · Trace": descendo uma coluna se lê onde o pacote está;
 * atravessando uma linha se lê o que aconteceu com um objeto.
 *
 * **Ausência não é falha.** Fase que não aparece no rollup ainda não rodou, e é
 * renderizada como espera, não como erro. Confundir as duas faria um objeto no meio do
 * pipeline parecer quebrado.
 *
 * **A onda 7 fechou as colunas que o protótipo pedia e o dado não existia.** ✅ O anexo
 * de lacunas do próprio protótipo listava, tabela por tabela, o que faltava: duração por
 * fase, tipo de ator, ordem de build, paridade, tentativas. As ondas 1 a 6 construíram
 * exatamente essa lista, e aqui elas aparecem juntas:
 *
 * | Coluna | Vem de |
 * |---|---|
 * | ordem da linha | `cc_object_dependency` → ordem topológica (onda 6) |
 * | gates `M`/`C` | manifesto aprovado + objeto no change set (onda 4) |
 * | sub-linha da célula | `cc_deploy_result.duration_ms` (onda 2) |
 * | tag e tentativas | manifesto + `cc_correction_attempt` (ondas 1 e 4) |
 * | pílula de paridade | `cc_regression_case` (onda 5) |
 *
 * Uma célula do design segue sem dado: o estado `RUN` (o backend só grava fase
 * concluída, nunca "em execução").
 */

type CellKind = "ok" | "fail" | "wait";

// Paleta literal do design, mantida como tabela porque são os tons de estado do
// protótipo — os neutros e a marca saem dos tokens do app.
const CELL: Record<CellKind, { bg: string; bd: string; fg: string; label: string }> = {
  ok: { bg: "#f3faf6", bd: "#d3ecdf", fg: "#2b8f57", label: "OK" },
  fail: { bg: "#fdf2f2", bd: "#f3d3d5", fg: "#b02a31", label: "FAIL" },
  wait: { bg: "#fbfcfd", bd: "#eef1f5", fg: "#b9c2cf", label: "—" },
};

export function cellKind(phases: CcObjectDeployStatus["phases"], phase: CcPhase): CellKind {
  const v = phases[phase];
  if (v === undefined) return "wait";
  return v ? "ok" : "fail";
}

/**
 * Fases obrigatórias que ainda faltam para o objeto contar como concluído.
 *
 * Só as de `CC_PHASES`. Uma fase do vocabulário que não seja obrigatória (`delete`)
 * nunca aparece como pendência: ela não participa do veredito.
 */
export function pendingPhases(obj: CcObjectDeployStatus): CcRequiredPhase[] {
  return CC_PHASES.filter((p) => obj.phases[p] !== true);
}

/**
 * A falha que explica o objeto: a **primeira** da ordem canônica, com o texto do SAP.
 *
 * Primeira e não última porque as fases rodam em sequência: uma falha em `deploy`
 * derruba tudo que vem depois, e apontar `abap_unit` mandaria o dev depurar a
 * consequência em vez da causa.
 */
export function firstFailure(
  obj: CcObjectDeployStatus,
): { phase: CcRequiredPhase; message: string | null } | null {
  for (const phase of CC_PHASES) {
    if (obj.phases[phase] === false) {
      return { phase, message: obj.messages?.[phase] ?? null };
    }
  }
  return null;
}

/**
 * A matriz na ordem de build, e não alfabética.
 *
 * "sorted by build order" é literal no protótipo, e é a ordem certa: a linha de cima é
 * a que precisa passar para a de baixo poder rodar. Objeto fora do grafo vai para o fim,
 * em ordem estável — inventar uma posição para ele no meio da cadeia seria afirmar uma
 * dependência que ninguém declarou.
 */
export function sortByBuildOrder<T extends { object_name: string }>(
  objetos: T[],
  buildOrder: string[] | undefined,
): T[] {
  if (!buildOrder?.length) return objetos;
  const pos = new Map(buildOrder.map((n, i) => [n, i]));
  return [...objetos].sort((a, b) => {
    const pa = pos.get(a.object_name) ?? Number.MAX_SAFE_INTEGER;
    const pb = pos.get(b.object_name) ?? Number.MAX_SAFE_INTEGER;
    return pa === pb ? a.object_name.localeCompare(b.object_name) : pa - pb;
  });
}

/**
 * A ordem de build cobre as linhas exibidas?
 *
 * Exige que **todas** estejam no grafo. Cobrir metade produz uma lista meio ordenada e
 * meio por chegada, e chamá-la de "sorted by build order" é pior que não dizer nada:
 * quem lê passa a confiar numa sequência que não vale para o objeto do meio.
 */
export function ordenadaPorBuild(
  objetos: { object_name: string }[],
  buildOrder: string[] | undefined,
): boolean {
  if (!buildOrder?.length || !objetos.length) return false;
  const conhecidos = new Set(buildOrder);
  return objetos.every((o) => conhecidos.has(o.object_name));
}

/** Quantos objetos passaram em cada fase — o rodapé de coluna do protótipo. */
export function phaseTotals(objetos: CcObjectDeployStatus[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const p of CC_PHASES) {
    out[p] = objetos.filter((o) => o.phases[p] === true).length;
  }
  return out;
}

/** O veredito de paridade do objeto, ou `null` quando nenhum caso o cobre. */
export function parityOf(
  parity: CcParityStatus | undefined,
  objectName: string,
): { label: string; tone: string } | null {
  const casos = (parity?.cases ?? []).filter((c) => c.object_name === objectName);
  if (!casos.length) return null;
  if (casos.some((c) => c.verdict === "drift" || c.verdict === "error")) {
    return { label: "drift", tone: "#b02a31" };
  }
  if (casos.some((c) => c.verdict === "pending")) {
    return { label: "pending", tone: "var(--exed-ink-500)" };
  }
  return { label: `${casos.length}/${casos.length}`, tone: "#2b8f57" };
}

/**
 * O objeto tem manifesto **aprovado**?
 *
 * Não é `current_version !== null`: ✅ `_current_manifest_version` devolve "approved se
 * houver, senão a mais nova não substituída", então um manifesto que voltou a `draft`
 * — o que o `draft_class_migration` faz quando o alvo recusa o desenho — acenderia o
 * chip. O cabeçalho da coluna promete "aprovado", e o chip tem de cobrar isso.
 */
export function manifestApproved(
  manifests: { items: CcObjectManifestHistory[] } | undefined,
  objectName: string,
  objectType: string,
): boolean {
  // ⚠️ O tipo não é opcional: `get_manifest_history` agrupa por (nome, tipo), então
  // achar por nome devolve o primeiro da ordenação e acende o chip verde na linha do
  // artefato que não foi aprovado. Gate mostrando aprovado onde não há é a informação
  // errada mais cara desta tela.
  const h = manifests?.items.find(
    (i) => i.object_name === objectName && i.object_type === objectType,
  );
  if (!h || h.current_version === null) return false;
  return h.versions.some(
    (v) => v.version === h.current_version && v.status === "approved",
  );
}

/** A disposição decidida no manifesto vigente — a "tag" da linha no protótipo. */
export function dispositionOf(
  manifests: { items: CcObjectManifestHistory[] } | undefined,
  objectName: string,
  objectType: string,
): string | null {
  const h = manifests?.items.find(
    (i) => i.object_name === objectName && i.object_type === objectType,
  );
  const v = h?.versions.find((x) => x.version === h.current_version);
  const d = (v?.manifest?.decision ?? {}) as Record<string, unknown>;
  return d.disposition ? String(d.disposition) : null;
}

interface Props {
  projectId: string;
  packageId: number;
  changeSetId: number;
  /** O objeto aberto no inspetor, ou `null`. Par de propósito: ver `CcObjectRef`. */
  selected?: CcObjectRef | null;
  onSelect?: (ref: CcObjectRef) => void;
}

export default function FleetMatrix({
  projectId,
  packageId,
  changeSetId,
  selected,
  onSelect,
}: Props) {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useCcDeployStatus(projectId, changeSetId);
  const grafo = useCcGraph(projectId, packageId);
  const paridade = useCcParity(projectId, packageId);
  const manifestos = useCcManifests(projectId, packageId);
  const fixes = useCcCorrections(projectId, changeSetId);

  if (isLoading) {
    return (
      <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">
        {t("Loading deploy status...")}
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">
        {t("Could not read the deploy status for this change set.")}
      </div>
    );
  }

  const objects = sortByBuildOrder(data.objects, grafo.data?.build_order);
  const totais = phaseTotals(data.objects);
  const concluidos = data.objects.filter((o) => o.concluded).length;

  const mono = (size: string) => ({
    fontFamily: "var(--font-mono)",
    fontSize: size,
    letterSpacing: ".06em",
  });

  return (
    <div
      className="overflow-hidden rounded-xl bg-card"
      style={{
        border: "1px solid var(--exed-card-border)",
        boxShadow: "0 1px 2px rgba(8,37,76,.04)",
      }}
    >
      {/* toolbar */}
      <div
        className="flex flex-wrap items-center gap-2 px-3.5 py-2.5"
        style={{ borderBottom: `1px solid var(--exed-ink-50)` }}
      >
        {/* Com o change set no texto. O hero conta o escopo do PACOTE e a matriz conta
            o que está NESTE change set, e os dois números divergem legitimamente — um
            pacote de 5 objetos pode ter um change set com 1. Sem dizer de qual conjunto
            cada número fala, a tela parece se contradizer. */}
        <span className="text-sm font-semibold">
          {t("{{count}} objects in change set CS-{{cs}} · {{phases}} phases", {
            count: objects.length,
            cs: changeSetId,
            phases: CC_PHASES.length,
          })}
        </span>
        <div className="flex-1" />
        <span style={{ ...mono("10px"), color: "var(--exed-ink-400)" }}>
          {/* "sorted by build order" só é verdade quando existe grafo: dizer sem ele
              seria vender ordem que é acaso do desempate. */}
          {/* Só afirma a ordem quando ela cobre as linhas exibidas. O `build_order` sai
              de `cc_package_object` (legados) mais os extremos das arestas, e esses
              conjuntos podem ser disjuntos dos objetos alvo: nesse caso a lista está em
              ordem de chegada, e dizer "ordenada" seria vender critério que não houve. */}
          {ordenadaPorBuild(objects, grafo.data?.build_order)
            ? t("{{n}} of {{total}} shown · sorted by build order", {
                n: objects.length,
                total: data.objects.length,
              })
            : tp(t, objects.length, "1 object", "{{n}} objects")}
        </span>
      </div>

      {objects.length === 0 ? (
        <div className="px-4 py-8 text-center text-sm text-muted-foreground">
          {t("This change set has no objects yet.")}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            {/* A promessa da tela é "uma linha é um objeto, uma coluna é uma fase". Com
                nove colunas, é exatamente isso que se perde sem cabeçalho declarado. */}
            <caption className="sr-only">
              {t("One row per object, one column per phase")}
            </caption>
            <thead>
              <tr
                style={{
                  background: CC_TABLE_HEAD_BG,
                  borderBottom: `1px solid var(--exed-card-border)`,
                }}
              >
                <th
                  scope="col"
                  className="sticky left-0 px-3.5 py-2.5 text-left uppercase"
                  style={{
                    ...mono("9px"),
                    letterSpacing: ".1em",
                    color: "var(--exed-ink-400)",
                    background: CC_TABLE_HEAD_BG,
                  }}
                >
                  {t("Object · build order")}
                </th>
                <th
                  scope="col"
                  className="px-2 py-2.5 text-center uppercase"
                  style={{
                    ...mono("9px"),
                    letterSpacing: ".1em",
                    color: "var(--exed-ink-400)",
                    borderLeft: `1px solid ${CC_COL_DIVIDER}`,
                  }}
                  title={t("manifest approved · object in change set")}
                >
                  {t("Gates")}
                </th>
                {CC_PHASES.map((p) => (
                  <th
                    key={p}
                    scope="col"
                    className="px-1 py-2.5 text-center uppercase"
                    style={{
                      ...mono("9px"),
                      letterSpacing: ".1em",
                      color: "var(--exed-ink-400)",
                      borderLeft: `1px solid ${CC_COL_DIVIDER}`,
                    }}
                  >
                    {p}
                  </th>
                ))}
                <th
                  scope="col"
                  className="px-2 py-2.5 text-center uppercase"
                  style={{
                    ...mono("9px"),
                    letterSpacing: ".1em",
                    color: "var(--exed-ink-400)",
                    borderLeft: `1px solid ${CC_COL_DIVIDER}`,
                  }}
                >
                  {t("Parity")}
                </th>
              </tr>
            </thead>

            <tbody>
              {objects.map((o, i) => {
                const pending = pendingPhases(o);
                const failure = firstFailure(o);
                const par = parityOf(paridade.data, o.object_name);
                const disp = dispositionOf(manifestos.data, o.object_name, o.object_type);
                const corr = fixes.data?.objects.find(
                  (c) => c.object_name === o.object_name,
                );
                const naOrdem = grafo.data?.build_order?.includes(o.object_name);
                return (
                  <tr
                    key={`${o.object_name}/${o.object_type}`}
                    onClick={() => onSelect?.(refOf(o))}
                    // Sem isto, abrir o inspetor de um objeto só existe para quem tem
                    // mouse: a linha é a ÚNICA porta para ele.
                    onKeyDown={
                      onSelect
                        ? (e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              onSelect(refOf(o));
                            }
                          }
                        : undefined
                    }
                    tabIndex={onSelect ? 0 : undefined}
                    role={onSelect ? "button" : undefined}
                    aria-pressed={onSelect ? sameObject(selected, refOf(o)) : undefined}
                    className={onSelect ? "cursor-pointer hover:bg-muted/40" : ""}
                    style={{
                      borderBottom: `1px solid ${CC_ROW_DIVIDER}`,
                      background:
                        sameObject(selected, refOf(o)) ? "var(--exed-ink-50)" : undefined,
                    }}
                  >
                    <td className="sticky left-0 bg-card px-3.5 py-2.5 align-top">
                      <div className="flex gap-2.5">
                        {/* A posição na ordem de build. Só aparece quando o objeto está
                            no grafo: número em objeto sem dependência declarada seria
                            uma ordem inventada. */}
                        <span
                          className="w-3 pt-0.5"
                          // ink-400 e não o mono apagado: a posição na ordem de build é
                          // dado, e a 2.3:1 ela não se lê.
                          style={{ ...mono("9px"), color: "var(--exed-ink-400)" }}
                        >
                          {naOrdem ? i + 1 : ""}
                        </span>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span
                              style={{
                                fontFamily: "var(--font-mono)",
                                fontSize: "12px",
                                fontWeight: 600,
                                color: "var(--exed-ink-900)",
                              }}
                            >
                              {o.object_name}
                            </span>
                            <span
                              className="rounded px-1.5 py-0.5"
                              style={{
                                ...mono("8.5px"),
                                color: "var(--exed-ink-500)",
                                background: CC_COL_DIVIDER,
                              }}
                            >
                              {o.object_type}
                            </span>
                          </div>

                          <div className="mt-1 flex flex-wrap items-center gap-1.5">
                            {disp && (
                              <span
                                data-testid="row-disposition"
                                className="rounded-full px-1.5 py-0.5"
                                style={{
                                  ...mono("9px"),
                                  background: "var(--exed-ink-50)",
                                  color: "var(--exed-ink-500)",
                                }}
                              >
                                {disp}
                              </span>
                            )}
                            {/* Tentativas: três correções parecem um vermelho só sem
                                este número — é a frase do anexo de lacunas do design. */}
                            {corr && corr.attempts > 0 && (
                              <span
                                data-testid="row-attempts"
                                // O comentário acima diz que sem este número três
                                // correções parecem um vermelho só — então ele precisa
                                // ser legível, não decorativo.
                                style={{ ...mono("9px"), color: "var(--exed-ink-500)" }}
                              >
                                {/* A ressalva do escopo não pode ser só `title`: some no
                                    toque e não é alcançável por teclado. A contagem passa
                                    a dizer o escopo no próprio texto (#603). */}
                                {t("attempts:{{n}} in this change set", {
                                  n: corr.attempts,
                                })}
                              </span>
                            )}
                          </div>

                          {failure ? (
                            <div
                              data-testid="failure-reason"
                              title={failure.message ?? undefined}
                              className="mt-1 line-clamp-2 max-w-[42ch] break-words text-[11px] leading-snug"
                              style={{ color: CELL.fail.fg }}
                            >
                              {failure.message
                                ? t("{{phase}}: {{message}}", {
                                    phase: failure.phase,
                                    message: failure.message,
                                  })
                                : t("{{phase}} failed, no message from SAP", {
                                    phase: failure.phase,
                                  })}
                            </div>
                          ) : (
                            !o.concluded &&
                            pending.length > 0 && (
                              <span className="text-[11px] text-muted-foreground">
                                {tp(t, pending.length, "1 phase pending", "{{n}} phases pending")}
                              </span>
                            )
                          )}
                        </div>
                      </div>
                    </td>

                    {/* gates M (manifesto aprovado) e C (objeto no change set) */}
                    <td
                      className="px-2 align-middle"
                      style={{ borderLeft: `1px solid ${CC_ROW_DIVIDER}` }}
                    >
                      <div className="flex items-center justify-center gap-1">
                        <GateChip
                          letter="M"
                          ok={manifestApproved(manifestos.data, o.object_name, o.object_type)}
                          titulo={
                            manifestApproved(
                              manifestos.data,
                              o.object_name,
                              o.object_type,
                            )
                              ? t("manifest approved")
                              : t("no approved manifest")
                          }
                        />
                        <GateChip letter="C" ok titulo={t("in this change set")} />
                      </div>
                    </td>

                    {CC_PHASES.map((p) => {
                      const kind = cellKind(o.phases, p);
                      const c = CELL[kind];
                      const message = o.messages?.[p];
                      const actor = o.actors?.[p];
                      const took = formatDuration(o.durations?.[p]);
                      const title =
                        `${o.object_name} (${o.object_type}) · ${p}: ${c.label}` +
                        (actor ? ` · ${actor}` : "") +
                        (message ? `\n${message}` : "");
                      return (
                        <td
                          key={p}
                          className="px-1 py-2 align-middle"
                          style={{ borderLeft: `1px solid ${CC_ROW_DIVIDER}` }}
                        >
                          <div
                            title={title}
                            className="rounded-md px-0.5 py-1.5 text-center"
                            style={{
                              background: c.bg,
                              border: `1px solid ${c.bd}`,
                              color: c.fg,
                              fontFamily: "var(--font-mono)",
                              fontSize: "9.5px",
                              fontWeight: 600,
                              letterSpacing: ".05em",
                            }}
                          >
                            {c.label}
                            {/* A sub-linha do protótipo é a duração. Fase não medida fica
                                sem nada: "0.0s" diria instantâneo. */}
                            {took && (
                              <div
                                data-testid={`phase-duration-${p}`}
                                style={{
                                  fontSize: "9px",
                                  fontWeight: 500,
                                  // Sem opacidade: a 0.7 sobre a célula tingida o número
                                  // ficava em 2.5:1 e virava mancha. É dado medido, não
                                  // enfeite — e não há outro lugar onde ele apareça.
                                  opacity: 1,
                                  marginTop: 2,
                                  letterSpacing: 0,
                                }}
                              >
                                {took}
                              </div>
                            )}
                          </div>
                        </td>
                      );
                    })}

                    <td
                      className="px-2 text-center align-middle"
                      style={{ borderLeft: `1px solid ${CC_ROW_DIVIDER}` }}
                    >
                      {par ? (
                        <span
                          data-testid="row-parity"
                          className="rounded-full px-2 py-1"
                          style={{
                            ...mono("9px"),
                            color: par.tone,
                            border: `1px solid ${par.tone}33`,
                          }}
                        >
                          {par.label}
                        </span>
                      ) : (
                        // Sem caso derivado não há paridade a afirmar. Um traço é honesto;
                        // um verde ali seria o falso positivo que a CC.28 registrou.
                        <span style={{ ...mono("9px"), color: CC_HAIRLINE }}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>

            {/* rodapé de coluna: quantos passaram em cada fase */}
            <tfoot>
              <tr style={{ background: CC_TABLE_HEAD_BG }}>
                <td
                  className="sticky left-0 px-3.5 py-2.5 uppercase"
                  style={{
                    ...mono("9px"),
                    letterSpacing: ".1em",
                    color: "var(--exed-ink-400)",
                    background: CC_TABLE_HEAD_BG,
                  }}
                >
                  <span className="flex items-center gap-1.5">
                    {t("Cleared")}
                    <span
                      data-testid="matrix-cleared"
                      className="inline-flex h-4 w-4 items-center justify-center rounded-full"
                      style={{
                        background: "var(--exed-gold)",
                        color: "var(--exed-navy)",
                        fontSize: "9px",
                        fontWeight: 700,
                      }}
                    >
                      {concluidos}
                    </span>
                  </span>
                </td>
                <td style={{ borderLeft: `1px solid ${CC_COL_DIVIDER}` }} />
                {CC_PHASES.map((p) => (
                  <td
                    key={p}
                    data-testid={`phase-total-${p}`}
                    className="px-1 py-2.5 text-center"
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: "12px",
                      fontWeight: 600,
                      color:
                        totais[p] === data.objects.length
                          ? CELL.ok.fg
                          : "var(--exed-ink-500)",
                      borderLeft: `1px solid ${CC_COL_DIVIDER}`,
                    }}
                  >
                    {totais[p]}
                    <span
                      style={{ fontSize: "9px", color: "var(--exed-ink-400)", fontWeight: 400 }}
                    >
                      /{data.objects.length}
                    </span>
                  </td>
                ))}
                <td style={{ borderLeft: `1px solid ${CC_COL_DIVIDER}` }} />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

/** O selo quadrado de gate (M/C) do protótipo. */
function GateChip({ letter, ok, titulo }: { letter: string; ok: boolean; titulo: string }) {
  return (
    <span
      title={titulo}
      className="flex h-[22px] w-[22px] items-center justify-center rounded-md"
      style={{
        background: ok ? CELL.ok.bg : CELL.wait.bg,
        color: ok ? CELL.ok.fg : CELL.wait.fg,
        fontFamily: "var(--font-mono)",
        fontSize: "9px",
        fontWeight: 600,
      }}
    >
      {letter}
    </span>
  );
}
